import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  modalClose: '[data-close-modal]',
};

/**
 * Opens a modal by its ID
 * @param {string} modalId - The ID of the modal to open
 * @returns {boolean} - Returns true if modal was opened successfully, false if modal not found
 *
 * Emits:
 *   'modal:opened' (CustomEvent) on the modal element when opened. Event detail contains { id: modalId }
 */
export function openModal(modalId) {
  const modal = document.getElementById(modalId);

  if (!modal) {
    console.warn(`Modal with ID "${modalId}" not found`);
    return false;
  }

  modal.showModal();
  disablePageScroll();

  // Emit custom event when modal is opened
  modal.dispatchEvent(
    new CustomEvent('modal:opened', {
      detail: { id: modalId },
    }),
  );

  // Helper to close modal and cleanup listeners
  const handleCloseModal = () => {
    closeModal(modalId);
    modal.removeEventListener('click', backdropHandler);
    const modalClose = modal.querySelector(selectors.modalClose);
    if (modalClose) {
      modalClose.removeEventListener('click', handleCloseModal);
    }
  };

  // Backdrop handler needs to be referenced for removal
  const backdropHandler = backdropEvent => {
    if (backdropEvent.target === modal) {
      handleCloseModal();
    }
  };

  // Attach listeners
  modal.addEventListener('click', backdropHandler);
  const modalClose = modal.querySelector(selectors.modalClose);
  if (modalClose) {
    modalClose.addEventListener('click', handleCloseModal);
  }

  return true;
}

/**
 * Closes a modal by its ID
 * @param {string} modalId - The ID of the modal to close
 * @returns {boolean} - Returns true if modal was closed successfully, false if modal not found
 *
 * Emits:
 *   'modal:closed' (CustomEvent) on the modal element when closed. Event detail contains { id: modalId }
 */
export function closeModal(modalId) {
  const modal = document.getElementById(modalId);

  if (!modal) {
    console.warn(`Modal with ID "${modalId}" not found`);
    return false;
  }

  modal.close();

  enablePageScroll();

  // Emit custom event when modal is closed
  modal.dispatchEvent(
    new CustomEvent('modal:closed', {
      detail: { id: modalId },
    }),
  );

  return true;
}
